import useApiHook from "Hooks/ApiHook";
import CustomerContactSelect from "components/Customer/CustomerContactSelect";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import {
  PICKUP_ADDRESS,
  isPickupMethod,
} from "../LotApproval/pickupAddress";
import UpdateWOCustomer from "./UpdateWOCustomer";

export default function WOinfo({
  formData,
  setFormData,
  refreshFunc,
  selectedWorkOrder,
  disableCustomerSelect,
  updateFunc,
}) {
  const dispatch = useDispatch();

  /*   All States
   ********************************************* */

  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [transitDays, setTransitDays] = useState(null);

  /*   All Functions
   ********************************************* */

  const getShippingMethods = async () => {
    const response = await API.get("/shipmentmethods", {
      headers: authHeader(),
    });
    if (response.status === 200) {
      return response;
    } else {
      throw new Error(response.message);
    }
  };

  const [loading, setLoading] = useState(false);

  const {
    data: shippingMethodsList,
    loading: shippingMethodsLoading,
    error: shippingMethodsFetchError,
  } = useApiHook(() => {
    return getShippingMethods();
  });

  // What the order's shipping address was before pickup replaced it, so
  // changing the method back puts it there instead of leaving the order
  // shipping to our own door. Held for this visit only -- after a reload the
  // old address is gone and the Shipping Address card's Edit/Choose is the
  // way back.
  const shipAddressBeforePickup = useRef(null);

  /*   Shipment method -> shipping address
   *
   * Switching to a pickup method also rewrites the order's shipping address
   * to the address it is collected from, matching what the customer approval
   * page does. The address rides along in `formData` and is written by the
   * SAME save as the rest of this form (see detailsPage updateWorkOrderData),
   * so Save is still one action and nothing is written behind the user.
   ********************************************* */
  const handleShipmentMethodChange = (rawValue) => {
    const nextId = rawValue === "null" ? null : parseInt(rawValue, 10);
    const nameOf = (id) =>
      shippingMethodsList?.find((m) => String(m.id) === String(id))
        ?.shippingMethod;

    const wasPickup = isPickupMethod(nameOf(formData?.shipmentMethod));
    const nowPickup = isPickupMethod(nameOf(nextId));

    setFormData((prev) => {
      const next = { ...prev, shipmentMethod: nextId };

      // The address the order holds right now, parsed -- older rows store it
      // as a JSON string.
      const stored =
        typeof selectedWorkOrder?.shippingAddress === "string"
          ? (() => {
              try {
                return JSON.parse(selectedWorkOrder.shippingAddress) || {};
              } catch {
                return {};
              }
            })()
          : selectedWorkOrder?.shippingAddress || {};

      if (nowPickup && !wasPickup) {
        shipAddressBeforePickup.current = prev.shippingAddress || stored;
        // MERGED, not replaced: the stored address also carries transit days,
        // signature release, selfCollect and serviceId, and overwriting the
        // whole object would silently drop them.
        next.shippingAddress = { ...stored, ...PICKUP_ADDRESS };
      } else if (!nowPickup && wasPickup && shipAddressBeforePickup.current) {
        next.shippingAddress = shipAddressBeforePickup.current;
        shipAddressBeforePickup.current = null;
      }

      return next;
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectContact = (contact) => {
    //     {
    //     "id": 27,
    //     "contactId": 4,
    //     "companyId": 3,
    //     "customerId": 14,
    //     "sortId": 3,
    //     "firstName": "Alex",
    //     "lastName": "Javaid",
    //     "department": null,
    //     "phone": "12345678910",
    //     "mobile": null,
    //     "email": "info@printsportswear.com",
    //     "smsNotification": false,
    //     "createdAt": "2025-10-14T11:24:07.000Z",
    //     "updatedAt": "2025-10-14T11:42:36.000Z"
    // }
    setFormData((prev) => {
      return {
        ...prev,
        firstName: contact?.firstName,
        lastName: contact?.lastName,
        department: contact?.department,
        phone: contact?.phone,
        email: contact?.email,
      };
    });
    setShowContactModal(false);
  };

  const getDate = (inputDate) => {
    const date = new Date(inputDate);
    if (isNaN(date.getTime())) {
      return null; // Invalid date
    }
    return date?.toISOString().slice(0, 10).replace("T", " ");
  };

  const getShipData = (wo, dueDate) => {
    try {
      let shippingAddress =
        typeof wo?.shippingAddress === "string"
          ? JSON.parse(wo?.shippingAddress)
          : wo?.shippingAddress || {};

      let shipDate = new Date(dueDate);
      shipDate.setDate(
        shipDate.getDate() - shippingAddress?.addressGroundTransitDays,
      );

      // Check if the date is valid
      if (isNaN(shipDate.getTime())) {
        return null; // Invalid date
      }

      return shipDate?.toISOString().slice(0, 10).replace("T", " ");
    } catch (err) {
      console.log(err.message);
      return 0;
    }
  };

  const setWoShipDate = async (e) => {
    setErrors({});

    let shippingAddress =
      typeof selectedWorkOrder?.shippingAddress === "string"
        ? JSON.parse(selectedWorkOrder?.shippingAddress)
        : selectedWorkOrder?.shippingAddress || {};

    if (shippingAddress?.addressGroundTransitDays) {
      setTransitDays(shippingAddress?.addressGroundTransitDays);

      let shipDate = getShipData(selectedWorkOrder, formData?.WoDueDate);

      if (shipDate === null) {
        setErrors({ ...errors, addressGroundTransitDays: "Invalid date" });
        return;
      }

      await setFormData((prev) => ({
        ...prev,
        WoShipDate: getDate(shipDate),
      }));

      console.log("Ship Date", shipDate);

      // also update the selectedWorkOrder in database
      // await updateFunc(e, shipDate);
    } else {
      setErrors({
        ...errors,
        addressGroundTransitDays: "Transit days not set",
      });
    }
  };

  const handleShippingMethodChange = async (e) => {
    try {
      let selectedShippingMethod = shippingMethodsList.find(
        (method) => method.id === parseInt(e.target.value),
      );

      let _shippingAddress = {
        addressLine1: "278 LAREDO DR",
        addressLine2: "",
        city: "DECATUR",
        state: "GA",
        zip: "30030",
        country: "US",
      };

      let body = {
        id: selectedWorkOrder.id,
      };

      // if "pickup" in selectedShippingMethod.shippingMethod.toLowerCase()
      if (
        selectedShippingMethod?.shippingMethod.toLowerCase().includes("pickup")
      ) {
        body = {
          ...body,
          shippingAddress: JSON.stringify(_shippingAddress),
        };
      }

      const { data } = await API.patch(
        "/work-orders",
        {
          data: body,
          id: selectedWorkOrder.id,
        },
        { headers: authHeader() },
      );

      if (data.message.type === "success") {
        await refreshFunc();

        setLoading(false);
        return;
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    if (shippingMethodsFetchError) {
      dispatch(
        setNotification({
          message: shippingMethodsFetchError.message,
          type: "error",
        }),
      );
    }
  }, [shippingMethodsFetchError]);

  useEffect(() => {
    setErrors({});

    let data =
      typeof selectedWorkOrder?.contactData === "string"
        ? JSON.parse(selectedWorkOrder?.contactData)
        : selectedWorkOrder?.contactData;

    let shippingAddress =
      typeof selectedWorkOrder?.shippingAddress === "string"
        ? JSON.parse(selectedWorkOrder?.shippingAddress)
        : selectedWorkOrder?.shippingAddress || {};

    if (shippingAddress?.addressGroundTransitDays) {
      setTransitDays(shippingAddress?.addressGroundTransitDays);
    }

    let dueDate = selectedWorkOrder?.WoDueDate;
    // if (!dueDate) {
    //   dueDate = new Date();
    //   dueDate.setDate(dueDate.getDate() + 1);
    // }

    const defaultMethodId = shippingMethodsList?.find(
      (method) => method.isDefault === true,
    )?.id;

    setFormData({
      firstName: data?.firstName,
      lastName: data?.lastName,
      department: data?.department,
      phone: data?.phone,
      email: data?.email,
      jobName: selectedWorkOrder?.jobName,
      subJobName: selectedWorkOrder?.subJobName,
      WoDueDate: dueDate ? getDate(dueDate) : "",
      WoShipDate: selectedWorkOrder?.WoShipDate
        ? getDate(selectedWorkOrder?.WoShipDate)
        : "",
      shipmentMethod:
        selectedWorkOrder?.shipmentMethod || defaultMethodId || null,
    });
  }, [selectedWorkOrder, shippingMethodsList]);

  // If shippingMethodsList loads after formData is already set and shipmentMethod is still empty,
  // populate it with the default shipping method so saving works correctly.
  useEffect(() => {
    if (!formData?.shipmentMethod && shippingMethodsList?.length) {
      const defaultMethodId = shippingMethodsList.find(
        (method) => method.isDefault === true,
      )?.id;
      if (defaultMethodId) {
        setFormData((prev) => ({
          ...prev,
          shipmentMethod: defaultMethodId,
        }));
      }
    }
  }, [shippingMethodsList]);

  // useEffect(() => {
  //   // console.log(selectedWorkOrder);

  //   let timeout = setTimeout(() => {
  //     if (selectedWorkOrder?.customer && showContactModal) {
  //       getCustomerById(selectedWorkOrder?.customer?.customerId);
  //     }
  //   }, 50);
  //   return () => clearTimeout(timeout);
  // }, [selectedWorkOrder, showContactModal]);

  return (
    <div className="bg-white custom-shadow flex-grow-1 px-3 pb-1 pt-3 custom-border-radius-sm">
      {shippingMethodsLoading && <Spinner variant="primary" />}
      {/*   Customer Contact
       ********************************************* */}
      <div className="d-flex align-items-center justify-content-between gap-2">
        <input
          type="text"
          className="form-control"
          value={formData?.firstName || ""}
          placeholder="Enter first name"
          name="firstName"
          onChange={handleChange}
        />

        <input
          type="text"
          className="form-control"
          value={formData?.lastName || ""}
          placeholder="Enter last name"
          name="lastName"
          onChange={handleChange}
        />

        <input
          type="text"
          className="form-control"
          value={formData?.department || ""}
          placeholder="Enter department"
          name="department"
          onChange={handleChange}
        />

        <input
          type="text"
          className="form-control"
          value={formData?.phone || ""}
          placeholder="Enter phone"
          name="phone"
          onChange={handleChange}
        />

        <input
          type="text"
          className="form-control"
          value={formData?.email || ""}
          placeholder="Enter email"
          name="email"
          onChange={handleChange}
        />
        <div className="d-flex align-items-center gap-1">
          <button
            disabled={!disableCustomerSelect}
            className="btn btn-info text-nowrap"
            onClick={() => setShowCustomerModal(true)}
          >
            Change Customer
          </button>
          <button
            disabled={!disableCustomerSelect}
            className="btn btn-info text-nowrap"
            onClick={() => setShowContactModal(true)}
          >
            Edit Contact
          </button>
        </div>
      </div>
      {/*   WO Info
       **********************************************/}
      {loading && <Spinner />}
      <div className="my-2">
        <div className="d-flex justify-content-between align-items-center gap-2">
          <div className="w-100">
            <input
              type="text"
              className="form-control "
              value={formData?.jobName || ""}
              placeholder="Enter Job Name"
              name="jobName"
              onChange={handleChange}
            />
          </div>
          <div className="w-75">
            <input
              type="text"
              style={{}}
              value={formData?.subJobName || ""}
              name="subJobName"
              onChange={handleChange}
              placeholder="Enter Sports Name"
              className="form-control"
            />
          </div>
          {/* Due Date / Needed By */}
          {/* <div className="d-flex align-items-center w-50">
            <div>
              <label className="me-2 text-nowrap ">Due Date</label>
            </div>
            <input
              className="form-control mx-0"
              placeholder="Enter address company"
              type="date"
              value={formData?.WoDueDate}
              onChange={async (e) => {
                setFormData({
                  ...formData,
                  WoDueDate: e.target.value,
                });
              }}
            />
          </div> */}

          {/* Shipment Method */}
          <div className="d-flex align-items-center gap-2 w-50">
            <div>
              <label className="text-nowrap">Shipment Method</label>
            </div>

            <div className="w-100">
              <select
                value={formData?.shipmentMethod || "null"}
                onChange={(e) => handleShipmentMethodChange(e.target.value)}
                className="form-select w-100"
              >
                <option value={"null"}>None</option>
                {shippingMethodsList?.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.shippingMethod}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/*   Customer Select Modal
       ********************************************* */}
      <Modal
        size="xl"
        contentClassName="bg-transparent"
        show={showCustomerModal}
        onHide={() => setShowCustomerModal(false)}
      >
        <Modal.Body>
          <UpdateWOCustomer
            selectedWorkOrder={selectedWorkOrder}
            showModal={setShowCustomerModal}
            refreshFunc={refreshFunc}
            defaultShipmentMethodId={
              shippingMethodsList?.find((m) => m.isDefault)?.id || null
            }
          />
        </Modal.Body>
      </Modal>
      {/*   Customer Contact Modal
       ********************************************* */}
      <Modal
        size="xl"
        contentClassName="bg-transparent"
        show={showContactModal}
        onHide={() => setShowContactModal(false)}
      >
        <Modal.Body>
          <CustomerContactSelect
            handleSelectContact={handleSelectContact}
            customerId={selectedWorkOrder?.customerId}
            isPrimaryId={true}
          />
          {/* <ContactSelectModal
            setFormData={setFormData}
            contactsList={contactsList}
            showModal={setShowContactModal}
          /> */}
        </Modal.Body>
      </Modal>
    </div>
  );
}
